/* eslint-disable react/jsx-props-no-spreading */
import { Box, HStack, Input } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import CircleEditButton from '@/client/components/shared/buttons/CircleEditButton';
import { Close } from '@/client/components/icons/ContinuIcons';
import ImageEditor from '@/client/components/shared/media/images/ImageEditor';
import User from '@/client/services/api/User';
import type { User as UserType } from '@/client/types/User';
import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { useLearnerProfileStore } from './state/learnerProfileStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

interface LearnerProfileHeroProps {
  userData: UserType;
}

export default function LearnerProfileHero({ userData }: LearnerProfileHeroProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { config } = useConfigStore();
  const { publicImageUrl } = config;
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const queryClient = useQueryClient();
  const heroInputRef = useRef<HTMLInputElement>(null);
  const { isActiveUser } = useLearnerProfileStore();
  const { banner_image: bannerImage } = userData;
  const [editBanner, setEditBanner] = useState('');

  const defaultBannerImage = `${publicImageUrl}continu/learner-profile-default-hero.svg`;

  const bannerImageUrl = `url(${bannerImage || defaultBannerImage})`;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let fileObject;

    if (event.target.files && event.target.files.length > 0) {
      fileObject = event.target.files?.[0];
    }

    if (!fileObject) {
      setToast({
        show: true,
        status: 'error',
        title: t('authoring.imageUploadError'),
      });
      return;
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = '';

    setEditBanner(URL.createObjectURL(fileObject));
  };

  const updateUserBannerImage = useMutation({
    mutationFn: async (url: string) => User.updateBannerImage(userId, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['learner-profile-user-data'] });
      setEditBanner('');

      setToast({
        show: true,
        status: 'success',
        title: t('modules.notifications.profile_updated'),
      });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('modules.notifications.canNotUpdateProfile'),
      }),
  });

  return (
    <Box>
      <Box
        width="full"
        height="206px"
        marginLeft="-1px"
        backgroundImage={bannerImageUrl}
        backgroundSize="cover"
        backgroundPosition="center center"
        backdropFilter="blur(76px)"
        backgroundBlendMode="screen"
        backgroundColor={bannerImage ? 'rgba(0, 0, 0, 0.15)' : 'brand.primary'}
        position="relative"
      >
        {isActiveUser && (
          <HStack position="absolute" top={3} right={3}>
            {!bannerImage && (
              <CircleEditButton
                tooltipLabel={`${t('learners.profile.hero.add')}`}
                onClick={() => heroInputRef.current?.click()}
              />
            )}
            {bannerImage && (
              <>
                <CircleEditButton
                  tooltipLabel={`${t('learners.profile.hero.edit')}`}
                  onClick={() => setEditBanner(bannerImage)}
                />

                <CircleEditButton
                  tooltipLabel={`${t('learners.profile.hero.remove')}`}
                  icon={Close}
                  onClick={() => updateUserBannerImage.mutateAsync('')}
                />
              </>
            )}
          </HStack>
        )}
      </Box>

      <Input
        display="none"
        ref={heroInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileChange(e)}
      />

      <ImageEditor
        image={editBanner}
        setImage={setEditBanner}
        onSuccessfulUpload={(cloudfrontLink) => updateUserBannerImage.mutateAsync(cloudfrontLink)}
      />
    </Box>
  );
}
